import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  Box,
  ButtonBase,
  Collapse,
  Link,
  Typography
} from "@mui/material";
import React, { useState } from "react";
import { Link as RouterLink } from "react-router-dom";

import { AI_WARNING } from "../../../common/colors";
import {
  RISK_WARNING_BANNER_FOOTER,
  RISK_WARNING_BANNER_SECTIONS,
  RISK_WARNING_BANNER_TITLE,
  RISK_WARNING_DISCLAIMER_LABEL
} from "../constants/riskWarning";

const styles = {
  container: {
    borderLeft: `6px solid ${AI_WARNING.border}`,
    overflow: "hidden"
  },
  toggleButton: {
    display: "flex",
    alignItems: "center",
    gap: 1,
    px: 1.75,
    py: 1.125,
    width: "100%",
    cursor: "pointer",
    userSelect: "none",
    textAlign: "left",
    bgcolor: AI_WARNING.background
  },
  warningIcon: {
    fontSize: 13,
    color: AI_WARNING.textPrimary
  },
  title: {
    fontSize: "12px",
    fontWeight: 500,
    color: AI_WARNING.textPrimary,
    flex: 1
  },
  expandIcon: (isExpanded: boolean) => ({
    fontSize: 14,
    color: AI_WARNING.textPrimary,
    transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
    transition: "transform 0.2s"
  }),
  content: {
    display: "flex",
    flexDirection: "column",
    gap: 1,
    bgcolor: AI_WARNING.contentBackground,
    px: 1.75,
    py: 1.5
  },
  sectionText: {
    fontSize: "12px",
    color: AI_WARNING.textSecondary,
    lineHeight: 1.6
  },
  sectionHead: {
    color: AI_WARNING.sectionHead,
    fontWeight: 600
  },
  footer: {
    display: "flex",
    alignItems: "baseline",
    justifyContent: "space-between",
    gap: 1.5,
    mt: 0.5,
    pt: 1,
    borderTop: `0.5px solid ${AI_WARNING.footerBorder}`
  },
  footerText: {
    fontSize: "11px",
    color: AI_WARNING.textItalic,
    fontStyle: "italic",
    fontWeight: 800
  },
  disclaimerLink: {
    fontSize: "11.5px",
    fontWeight: 700,
    color: AI_WARNING.linkColor,
    textDecoration: "underline",
    whiteSpace: "nowrap"
  }
} as const;

const RiskWarningBanner: React.FC = () => {
  const [isExpanded, setIsExpanded] = useState(false);

  const handleToggle = (): void => {
    setIsExpanded(prev => !prev);
  };

  return (
    <Box sx={styles.container}>
      <ButtonBase
        onClick={handleToggle}
        aria-expanded={isExpanded}
        aria-label="Toggle risk warning details"
        sx={styles.toggleButton}
      >
        <WarningAmberIcon sx={styles.warningIcon} />
        <Typography sx={styles.title}>
          {RISK_WARNING_BANNER_TITLE}
        </Typography>
        <ExpandMoreIcon sx={styles.expandIcon(isExpanded)} />
      </ButtonBase>

      <Collapse in={isExpanded}>
        <Box sx={styles.content}>
          {RISK_WARNING_BANNER_SECTIONS.map((section) => (
            <Typography key={section.head} sx={styles.sectionText}>
              <Box component="span" sx={styles.sectionHead}>
                {section.head}
              </Box>
              {" "}{section.body}
            </Typography>
          ))}

          <Box sx={styles.footer}>
            <Typography sx={styles.footerText}>
              {RISK_WARNING_BANNER_FOOTER}
            </Typography>
            <Link
              component={RouterLink}
              to="/ai-transparency"
              target="_blank"
              rel="noopener noreferrer"
              sx={styles.disclaimerLink}
            >
              {RISK_WARNING_DISCLAIMER_LABEL} →
            </Link>
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
};

export default RiskWarningBanner;
